import { useRef, useState } from 'react'

// ── Overlay plein écran pour l'aperçu ─────────────────────────
function PreviewOverlay({ onClose, imageUrl, children }) {
  const mouseDownOnBackdrop = useRef(false)
  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onMouseDown={e => { mouseDownOnBackdrop.current = e.target === e.currentTarget }}
      onMouseUp={e => { if (mouseDownOnBackdrop.current && e.target === e.currentTarget) onClose() }}
    >
      <button
        className="absolute top-4 right-4 text-white text-3xl w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 transition"
        onClick={onClose}
      >
        ×
      </button>
      <div
        className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl"
        onMouseDown={e => e.stopPropagation()}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900" />
        )}
        {children && (
          <div className="absolute inset-0">{children}</div>
        )}
      </div>
    </div>
  )
}

/**
 * Carte visuelle mutualisée — ratio 16/9.
 *
 * Props :
 *   imageUrl       — image de fond (optionnel)
 *   headerContent  — contenu JSX en haut à gauche (titre, badges…)
 *   overlayContent — contenu JSX superposé sur la carte (ex. tracé circuit)
 *   previewContent — contenu JSX affiché dans l'aperçu plein écran
 *                    (si undefined, le bouton Aperçu n'apparaît que si imageUrl)
 *   actions        — boutons JSX affichés au survol en bas à droite
 */
export function VisualCard({ imageUrl, headerContent, overlayContent, previewContent, actions }) {
  const [previewing, setPreviewing] = useState(false)
  const hasPreview = !!imageUrl || previewContent !== undefined

  return (
    <>
      <div className="relative aspect-video rounded-2xl overflow-hidden shadow-lg group">
        {/* Fond */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
            <span className="text-5xl opacity-20">🖼</span>
          </div>
        )}

        {/* Dégradé permanent */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/40" />

        {/* Contenu superposé (ex. tracé circuit) */}
        {overlayContent && (
          <div className="absolute inset-0">{overlayContent}</div>
        )}

        {/* En-tête */}
        {headerContent && (
          <div className="absolute top-0 left-0 right-0 p-3">{headerContent}</div>
        )}

        {/* Boutons au survol — bas de carte */}
        <div className="absolute bottom-0 left-0 right-0 px-3 py-2.5 flex gap-2 justify-end opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-200 bg-gradient-to-t from-black/60 to-transparent">
          {hasPreview && (
            <button
              onClick={() => setPreviewing(true)}
              className="bg-white/90 text-gray-800 text-xs font-medium px-3 py-1.5 rounded-lg shadow hover:bg-white transition"
            >
              🔍 Aperçu
            </button>
          )}
          {actions}
        </div>
      </div>

      {/* Aperçu plein écran */}
      {previewing && (
        <PreviewOverlay
          onClose={() => setPreviewing(false)}
          imageUrl={imageUrl}
        >
          {previewContent}
        </PreviewOverlay>
      )}
    </>
  )
}

// ── Carte "Ajouter" mutualisée ────────────────────────────────
export function AddCard({ onClick, label = 'Nouveau' }) {
  return (
    <button
      onClick={onClick}
      className="aspect-video rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all duration-200"
    >
      <span className="text-4xl">+</span>
      <span className="text-sm font-medium">{label}</span>
    </button>
  )
}
